/**
 * 「执行日期 → 发布时间」带出的**纯函数层**（两种模式共用一份实现）。
 *
 * 【业务链路】顶层「执行日期」(`project_plan_info.$begin_and_end_at`) 变更、或新增合作内容行时，
 * 把合作内容里「发布时间」这个动态属性（schema 的 `inner_code === RELEASE_TIME`）**为空**的行填上该周期。
 * 唯一的写入方是 `View.tsx` 的 `updateReleaseTime`（读 `planInfo` → `setPlanInfo`），
 * 卡片 / 表格两种模式共用这一份写逻辑；本文件只做「算」，不做「写」。
 *
 * 【两个触发点】（原始实现在 `c9d2f9e69`，两边都必须有）
 * - **T1**：用户改动「执行日期」→ `View.tsx` 的 `onValuesChange` → `updateReleaseTime(values)`（传本次变更值）；
 * - **T2**：新增合作内容行 → 延迟 `ADD_ROW_RELEASE_TIME_DELAY_MS` 后 `updateReleaseTime({}, true)`
 *   （`add=true` 分支改读 `form.getFieldsValue()` 全量值，因为此时没有「本次变更值」可用）。
 *   ⚠️ 延迟是必需的：新增行要先经 `onChange → planInfo` 落地，带出才有对象可写。
 *
 * 【为什么单独成文件】这段映射此前内联在 `View.tsx` 里无法单测，而它的失败方式是静默的
 * （覆盖了用户已填的值、或漏带出）。抽成纯函数后可用断言钉住不变量：
 * 「空的带出 / 已有值不动 / 非发布时间属性不动 / 两种模式同一实现」。
 * 逻辑与 `View.tsx` 原内联实现**逐字等价**，仅把常量抽出来。
 */
import { alignPropertyValues } from './propertyPipe';

/** 「发布时间」属性的后端 schema 标识（`property_schema.inner_code`） */
export const RELEASE_TIME_INNER_CODE = 'RELEASE_TIME';
/**
 * 新增合作内容后、触发发布时间带出的延迟（ms）。
 *
 * 【为什么必须延迟】新增行走的是 `onChange → CooperationFormList → NewCooperate → SplitForm.planInfo`
 * 这条回写链，`planInfo` 要下一轮才含新行；立刻带出会**没有对象可写**。
 * 卡片与表格两种模式共用此常量，保证两边时序一致（此前卡片写死 500、表格用同名常量，容易改漏一边）。
 */
export const ADD_ROW_RELEASE_TIME_DELAY_MS = 500;

/**
 * 给**单行**的「发布时间」属性带出执行日期。
 *
 * - 只动 `inner_code === RELEASE_TIME` 的属性，其余属性原样（含 `value` 为 `0`/`false` 等合法值）；
 * - **仅当该属性值为空时**才写（`!value?.length`）—— 已有值一律不覆盖；
 * - 结构与 `View.tsx` 原内联实现逐字一致（含 `property_values` 缺失时保留 `undefined`）。
 */
export const fillReleaseTimeForRow = <TRow extends Record<string, any>>(row: TRow, releaseTime: any): TRow => ({
  ...row,
  property_values: row?.property_values?.map((propertyValue: Record<string, any>) => ({
    ...propertyValue,
    value:
      propertyValue?.property_schema?.inner_code === RELEASE_TIME_INNER_CODE && !propertyValue?.value?.length
        ? releaseTime
        : propertyValue.value,
  })),
});

/**
 * 按行带出「发布时间」：把 `rows` 中所有「发布时间为空」的行填上 `releaseTime`。
 *
 * 与 `View.tsx` 原 `planInfo?.cooperate_accounts?.map(...)` 等价：`rows` 为 `undefined` 时结果也是
 * `undefined`（不凭空造空数组）。
 *
 * ⚠️ **执行日期不是合法区间（非长度为 2 的数组）时原样返回**：调用方（`View.tsx` 的
 * `updateReleaseTime`）本来就有 `$begin_and_end_at?.length === 2` 的门禁，此处再兜一道，
 * 是为了让「自动带出」这类**新入口**不会把 `undefined` 写进本来就空的属性
 *（语义上等于没写，但会白白换掉整行 `property_values` 的引用 → 多余重渲染）。
 */
export const fillReleaseTimeForRows = <TRow extends Record<string, any>>(
  rows: TRow[] | undefined,
  releaseTime: any,
): TRow[] | undefined => {
  if (!Array.isArray(releaseTime) || releaseTime.length !== 2) return rows;
  return rows?.map((row) => fillReleaseTimeForRow(row, releaseTime));
};

/**
 * 给一份「表单值」对象里的合作内容行补齐「发布时间」——**自动带出（初始化）路径专用**。
 *
 * 【为什么需要单独一个入口】策划拆单会有一条**自动带出**的合作内容行（自动拆分/草稿/退回草稿），
 * 它经 `SplitForm` 的 `setPlanInfo` 直接落地，**不走 `useRowMutations.addRow`、也不走卡片 add 按钮**，
 * 故触发点 T2 覆盖不到；而且带出发生在初始化阶段，**外层表单此时可能还没有执行日期**，
 * 用 `View.tsx` 那条「读整个 form」的路径会取空。故此处**直接取带出数据自带的
 * `project_plan_info.$begin_and_end_at`** —— 与行数据同源、天然一致，也没有时序竞争。
 *
 * 无有效周期时**返回原对象引用**（不换引用、不触发多余重渲染）；有周期时只填空、不覆盖已有值。
 */
export const fillReleaseTimeInFormValues = <T extends Record<string, any>>(formValues: T): T => {
  const releaseTime = formValues?.project_plan_info?.$begin_and_end_at;
  if (!Array.isArray(releaseTime) || releaseTime.length !== 2) return formValues;
  return {
    ...formValues,
    cooperate_accounts: fillReleaseTimeForRows(formValues?.cooperate_accounts, releaseTime),
  };
};

/**
 * 「初始化后一次性补填」：**先给没有属性集的行补出属性集，再补空白的「发布时间」**。
 *
 * 【为什么要补属性集】新建拆单时第一条合作内容是 `NewCooperate` 的
 * `planInfo?.cooperate_accounts || [{}]` **兜底造的空壳行**——它没有 `property_values`，
 * 连「发布时间」这个属性项都不存在，任何「填空」逻辑都无从下手。故先按 schema
 * `alignPropertyValues([], schema)` 补出完整属性集（含一个空的 RELEASE_TIME）再填。
 *
 * 【幂等】无需要变更时**返回入参原引用**（调用方据此跳过 setState，避免多余渲染）；
 * 判据是「该行是否真的需要补集 / 真的有空值要填」，不依赖对象引用比较。
 *
 * 执行日期非法或没有行时同样原样返回。
 */
export const fillReleaseTimeAfterInit = <TRow extends Record<string, any>>(
  rows: TRow[] | undefined,
  releaseTime: any,
  schema: any,
): TRow[] | undefined => {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  if (!Array.isArray(releaseTime) || releaseTime.length !== 2) return rows;
  let changed = false;
  const next = rows.map((row) => {
    const hasPropertyValues = Array.isArray(row?.property_values) && row.property_values.length > 0;
    const base = hasPropertyValues ? row : { ...row, property_values: alignPropertyValues([], schema) };
    if (!hasPropertyValues) changed = true;
    // 只在「确实有空的发布时间」时才写，避免无变更却换掉整行引用
    const needsFill = base.property_values?.some(
      (propertyValue: Record<string, any>) =>
        propertyValue?.property_schema?.inner_code === RELEASE_TIME_INNER_CODE &&
        !propertyValue?.value?.length,
    );
    if (!needsFill) return base;
    changed = true;
    return fillReleaseTimeForRow(base, releaseTime);
  });
  return changed ? next : rows;
};
